<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'tariffs',
    path: '/tariffs',
    component: 'Tariffs',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Tariffs',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onItemsChanged = (...args) => {
    $app.value.repo.clearCache('list_object_by_type', {
      parameters: { object_type: 'tariff' },
    });
  };

  const items = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));
  const b_1040167445267876_rowTemplate = computed(() => ({
    object_type: 'tariff',
  }));
  const b_1040167445267876_columns = computed(() => [
    {
      name: 'code',
      header: $fn.value.pgettext('Tariff', 'Code'),
      width: '80px',
      required: true,
    },
    {
      name: 'name',
      header: $fn.value.pgettext('Tariff', 'Name'),
      width: '200px',
      required: true,
    },
    {
      name: 'description',
      header: $fn.value.pgettext('Tariff', 'Description'),
    },
  ]);
  const b_1040167445267876_style = computed(() => ({
    minHeight: '200px',
  }));

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_1040167445267876_rowTemplate: b_1040167445267876_rowTemplate.value,
    b_1040167445267876_columns: b_1040167445267876_columns.value,
    b_1040167445267876_style: b_1040167445267876_style.value,
  }));

  const $$load_items = async () => {
    return $fn.value
      .fetch('list_object_by_type', { parameters: { object_type: 'tariff' } })
      .then((d) => _.sortBy(d, 'id'));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_1040167445267876_rowTemplate', nodeId: '1040167445267876' },
      { name: 'b_1040167445267876_columns', nodeId: '1040167445267876' },
      { name: 'b_1040167445267876_style', nodeId: '1040167445267876' },
    ],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: {
      items,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_1040167445267876_rowTemplate,
      b_1040167445267876_columns,
      b_1040167445267876_style,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: [$fn.value.pgettext('Tariffs|Header', 'Configure tariffs')],
      icon: $config.value.tariff.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_ru6m32v1m_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_script_u2062buln_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value
        .crud('object_by_type')
        .upsert($event.data.row)
        .then(onItemsChanged)
    );
  };
  const act_1040167445267876_script_u2062buln_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1040167445267876_script_u2062buln_value_callable($event);
      },
    };
  };
  const act_1040167445267876_script_w9usqizee_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value
        .crud('object_by_type')
        .delete($event.data.row)
        .then(onItemsChanged)
    );
  };
  const act_1040167445267876_script_w9usqizee_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1040167445267876_script_w9usqizee_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_ru6m32v1m =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_ru6m32v1m_getActionArgs,
    });
  const inst_evh_7315092382398562_close_qkl0dg5t4 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_ru6m32v1m,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_qkl0dg5t4],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_9cdmf01qn =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_3rdn5y59q =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_9cdmf01qn,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_3rdn5y59q],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_ofkhfd78t =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_9auqhi87s =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_ofkhfd78t,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_9auqhi87s],
    event: 'close',
  });
  const act_1040167445267876_script_u2062buln = new actionTypes.ScriptAction({
    actionArgs: act_1040167445267876_script_u2062buln_getActionArgs,
  });
  const inst_evh_1040167445267876_save_6kg0ywke7 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_script_u2062buln,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_1040167445267876_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_save_6kg0ywke7],
    event: 'save',
  });
  const act_1040167445267876_script_w9usqizee = new actionTypes.ScriptAction({
    actionArgs: act_1040167445267876_script_w9usqizee_getActionArgs,
  });
  const inst_evh_1040167445267876_delete_5rq8lv0b6 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_script_w9usqizee,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_1040167445267876_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_delete_5rq8lv0b6],
    event: 'delete',
  });
  const act_2140198529698299_closeComponent_9rqwfgjfh =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2140198529698299_clickPreventStop_z9fo0fe55 =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeComponent_9rqwfgjfh,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_z9fo0fe55],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_qkl0dg5t4,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_3rdn5y59q,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_9auqhi87s,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_save_6kg0ywke7,
    inst_evh_1040167445267876_save,
    inst_evh_1040167445267876_delete_5rq8lv0b6,
    inst_evh_1040167445267876_delete,
    inst_evh_2140198529698299_clickPreventStop_z9fo0fe55,
    inst_evh_2140198529698299_clickPreventStop,
  };

  const evh_7315092382398562_close_qkl0dg5t4 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_qkl0dg5t4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_3rdn5y59q = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_3rdn5y59q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_9auqhi87s = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_9auqhi87s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_save_6kg0ywke7 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_save_6kg0ywke7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_save = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_delete_5rq8lv0b6 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_delete_5rq8lv0b6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_delete = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_z9fo0fe55 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_z9fo0fe55.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Tariffs"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpDataTable
        tableId="Tariffs"
        editable
        :value="items"
        :rowTemplate="b_1040167445267876_rowTemplate"
        :columns="b_1040167445267876_columns"
        class="h-100"
        :style="b_1040167445267876_style"
        @save="evh_1040167445267876_save($event, {})"
        @delete="evh_1040167445267876_delete($event, {})"
      >
      </ItpDataTable>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
